import {
  API_BASE_URL,
  AUTH_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from "lib/apiUrl";
import {
  getSessionExpiredMessage,
  handleSessionExpired,
  isSessionExpiredError,
  isSessionExpiredPayload,
} from "lib/sessionExpired";

export class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/** Many backends wrap their JSON as `{ status: true|false, message, data }`.
 *  Treat the response as a success only when an explicit truthy flag is
 *  present, or when no flag is present at all (HTTP 2xx already implies ok). */
export function isSuccessResponse(resp) {
  if (!resp || typeof resp !== "object") return true;
  if (typeof resp.status === "boolean") return resp.status;
  if (typeof resp.success === "boolean") return resp.success;
  if (typeof resp.ok === "boolean") return resp.ok;
  return true;
}

/** Extracts `{ user, token, refreshToken }` from an auth response, regardless
 *  of whether the body is wrapped under `data` or returns flat keys, and
 *  whether the access token is named `token` or `accessToken`. */
export function extractAuthPayload(resp) {
  const root = resp?.data ?? resp ?? {};
  return {
    user: root.user ?? null,
    token: root.token ?? root.accessToken ?? null,
    refreshToken: root.refreshToken ?? null,
  };
}

const safeLocalStorage = () =>
  typeof window === "undefined" ? null : window.localStorage;

const readKey = (key) => {
  const ls = safeLocalStorage();
  return ls ? ls.getItem(key) : null;
};

const writeKey = (key, value) => {
  const ls = safeLocalStorage();
  if (!ls) return;
  if (value) ls.setItem(key, value);
  else ls.removeItem(key);
};

export function getAuthToken() {
  return readKey(AUTH_STORAGE_KEY);
}

export function setAuthToken(token) {
  writeKey(AUTH_STORAGE_KEY, token);
}

export function clearAuthToken() {
  setAuthToken(null);
}

export function getRefreshToken() {
  return readKey(REFRESH_TOKEN_STORAGE_KEY);
}

export function setRefreshToken(token) {
  writeKey(REFRESH_TOKEN_STORAGE_KEY, token);
}

export function clearRefreshToken() {
  setRefreshToken(null);
}

/** Clears both the access token and refresh token. */
export function clearAuthTokens() {
  clearAuthToken();
  clearRefreshToken();
}

function buildHeaders({ headers, body, auth }) {
  const finalHeaders = new Headers(headers || {});
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  if (body !== undefined && !isFormData && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }

  if (auth !== false) {
    const token = getAuthToken();
    if (token && !finalHeaders.has("Authorization")) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  return finalHeaders;
}

async function parseResponse(response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return response.json();
  return response.text();
}

/**
 * Low-level request helper. Resolves with parsed body on 2xx, otherwise
 * throws an `ApiError` carrying { status, data }.
 *
 * Options:
 *   - method, headers, body (object | FormData | string), signal
 *   - auth: false to skip the Authorization header for this call
 *   - any other valid `fetch` init keys
 */
export async function apiFetch(path, options = {}) {
  const { method = "GET", body, headers, auth, signal, ...rest } = options;
  const url = /^https?:\/\//i.test(path) ? path : `${API_BASE_URL}${path}`;

  const init = {
    method,
    headers: buildHeaders({ headers, body, auth }),
    signal,
    ...rest,
  };

  if (body !== undefined) {
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
    init.body = isFormData || typeof body === "string" ? body : JSON.stringify(body);
  }

  const response = await fetch(url, init);
  const data = await parseResponse(response);

  if (!response.ok) {
    const message =
      (data && typeof data === "object" && (data.message || data.error)) ||
      response.statusText ||
      `Request failed with status ${response.status}`;
    const error = new ApiError(message, { status: response.status, data });

    if (
      auth !== false &&
      isSessionExpiredError(error, { httpStatus: response.status })
    ) {
      await handleSessionExpired(
        getSessionExpiredMessage(error) || message
      );
    }

    throw error;
  }

  if (auth !== false && isSessionExpiredPayload(data)) {
    const message =
      getSessionExpiredMessage(data) ||
      (typeof data === "object" && (data.message || data.error)) ||
      "Your session is invalid or has expired. Please sign in again.";
    await handleSessionExpired(message);
    throw new ApiError(message, { status: response.status, data });
  }

  return data;
}

export const api = {
  get: (path, options) => apiFetch(path, { ...options, method: "GET" }),
  post: (path, body, options) => apiFetch(path, { ...options, method: "POST", body }),
  put: (path, body, options) => apiFetch(path, { ...options, method: "PUT", body }),
  patch: (path, body, options) => apiFetch(path, { ...options, method: "PATCH", body }),
  delete: (path, options) => apiFetch(path, { ...options, method: "DELETE" }),
};

export default api;
